/**
 * Godot SceneTree — central runtime: scene loading, process/physics loops,
 * groups, timers, deferred calls, singletons, pause.
 */

import type { SceneResource } from './types';
import { loadSceneByPath } from './storage';
import { loadProjectSettings } from './ProjectSettings';
import { EngineInput, setGlobalInput } from './InputMap';
import { Signal, SignalBus } from './Signal';
import type { ScriptHost } from './ScriptRuntime';

export type DeferredCall = () => void;

export interface SceneTimer {
  id: string;
  timeLeft: number;
  oneshot: boolean;
  waitTime: number;
  callback: () => void;
  paused: boolean;
}

export interface TreeAPI {
  paused: boolean;
  current_scene: string;
  root: string;
  /** Physics tick rate */
  physics_ticks_per_second: number;

  create_timer(seconds: number): Promise<void>;
  create_timer_callback(seconds: number, cb: () => void): string;
  call_deferred(fn: DeferredCall): void;
  call_group(group: string, method: string, ...args: unknown[]): void;
  get_nodes_in_group(group: string): unknown[];
  change_scene_to_file(path: string): Promise<boolean>;
  quit(): void;
  set_pause(p: boolean): void;
  get_frame(): number;
}

export type SingletonFactory = (tree: SceneTree) => unknown;

export class SceneTree {
  readonly input = new EngineInput();
  readonly treeSignal = new SignalBus();
  /** tree_changed, process_frame, physics_frame, scene_changed */
  readonly tree_changed = this.treeSignal.signal('tree_changed');
  readonly process_frame = this.treeSignal.signal('process_frame');
  readonly physics_frame = this.treeSignal.signal('physics_frame');
  readonly scene_changed = this.treeSignal.signal('scene_changed');

  private host: ScriptHost;
  private paused = false;
  private frame = 0;
  private physicsAccum = 0;
  private physicsDt: number;
  private deferred: DeferredCall[] = [];
  private timers: SceneTimer[] = [];
  private timerSeq = 0;
  private groups = new Map<string, Set<string>>(); // group -> node ids
  private nodeGroups = new Map<string, Set<string>>(); // node id -> groups
  private singletons = new Map<string, unknown>();
  private currentPath = '';
  private quitting = false;
  private running = false;

  /** External hooks filled by SceneRuntime */
  private getNodesInGroupImpl: ((group: string) => unknown[]) | null = null;
  private callGroupImpl: ((group: string, method: string, args: unknown[]) => void) | null = null;
  private changeSceneImpl: ((path: string) => Promise<boolean>) | null = null;

  constructor(host?: ScriptHost) {
    this.host = host || { log: (m) => console.debug('[SceneTree]', m) };
    const ps = loadProjectSettings();
    this.physicsDt = 1 / Math.max(1, ps.physics.common_physics_ticks_per_second || 60);
    setGlobalInput(this.input);
    this.input.map.fromJSON(ps.input);
  }

  setHost(host: ScriptHost): void {
    this.host = host;
  }

  log(msg: string): void {
    this.host.log(msg);
  }

  get is_running(): boolean {
    return this.running;
  }

  get is_paused(): boolean {
    return this.paused;
  }

  get current_scene_path(): string {
    return this.currentPath;
  }

  setCurrentPath(path: string): void {
    this.currentPath = path;
  }

  setRunning(v: boolean): void {
    this.running = v;
    if (v) {
      this.input.enable();
      this.frame = 0;
      this.physicsAccum = 0;
    } else {
      this.input.disable();
      this.timers = [];
      this.deferred = [];
    }
  }

  setPaused(p: boolean): void {
    this.paused = p;
  }

  /** Bind runtime implementations (called by SceneRuntime). */
  bindRuntime(hooks: {
    getNodesInGroup: (group: string) => unknown[];
    callGroup: (group: string, method: string, args: unknown[]) => void;
    changeScene: (path: string) => Promise<boolean>;
    onNodeFreed?: (id: string) => void;
  }): void {
    this.getNodesInGroupImpl = hooks.getNodesInGroup;
    this.callGroupImpl = hooks.callGroup;
    this.changeSceneImpl = hooks.changeScene;
    void hooks.onNodeFreed;
  }

  // ── Groups ─────────────────────────────────────────────────────

  add_to_group(nodeId: string, group: string): void {
    if (!this.groups.has(group)) this.groups.set(group, new Set());
    this.groups.get(group)!.add(nodeId);
    if (!this.nodeGroups.has(nodeId)) this.nodeGroups.set(nodeId, new Set());
    this.nodeGroups.get(nodeId)!.add(group);
  }

  remove_from_group(nodeId: string, group: string): void {
    this.groups.get(group)?.delete(nodeId);
    this.nodeGroups.get(nodeId)?.delete(group);
  }

  get_nodes_in_group_ids(group: string): string[] {
    return Array.from(this.groups.get(group) || []);
  }

  get_groups_of(nodeId: string): string[] {
    return Array.from(this.nodeGroups.get(nodeId) || []);
  }

  clear_node_groups(nodeId: string): void {
    const gs = this.nodeGroups.get(nodeId);
    if (gs) {
      for (const g of gs) this.groups.get(g)?.delete(nodeId);
    }
    this.nodeGroups.delete(nodeId);
  }

  clear_all_groups(): void {
    this.groups.clear();
    this.nodeGroups.clear();
  }

  // ── Singletons (autoload) ──────────────────────────────────────

  register_singleton(name: string, value: unknown): void {
    this.singletons.set(name, value);
  }

  get_singleton(name: string): unknown {
    return this.singletons.get(name);
  }

  has_singleton(name: string): boolean {
    return this.singletons.has(name);
  }

  // ── Deferred / timers ──────────────────────────────────────────

  call_deferred(fn: DeferredCall): void {
    this.deferred.push(fn);
  }

  create_timer(seconds: number): Promise<void> {
    return new Promise((resolve) => {
      this.create_timer_callback(seconds, () => resolve());
    });
  }

  create_timer_callback(seconds: number, cb: () => void, oneshot = true): string {
    const id = `timer_${++this.timerSeq}`;
    this.timers.push({
      id,
      timeLeft: seconds,
      waitTime: seconds,
      oneshot,
      callback: cb,
      paused: false,
    });
    return id;
  }

  kill_timer(id: string): void {
    this.timers = this.timers.filter((t) => t.id !== id);
  }

  // ── Frame update ───────────────────────────────────────────────

  /**
   * Advance tree services for one rendered frame.
   * Returns how many physics steps to run this frame.
   */
  begin_frame(dt: number): { process: boolean; physicsSteps: number; physicsDt: number } {
    if (!this.running) return { process: false, physicsSteps: 0, physicsDt: this.physicsDt };
    this.frame++;
    if (this.paused) {
      this.flush_deferred();
      this.input.end_frame();
      return { process: false, physicsSteps: 0, physicsDt: this.physicsDt };
    }

    // Timers
    for (const t of this.timers.slice()) {
      if (t.paused) continue;
      t.timeLeft -= dt;
      if (t.timeLeft <= 0) {
        try {
          t.callback();
        } catch (e) {
          this.host.log(`Timer error: ${(e as Error).message}`);
        }
        if (t.oneshot) this.kill_timer(t.id);
        else t.timeLeft = t.waitTime;
      }
    }

    this.physicsAccum += dt;
    let steps = 0;
    const maxSteps = 8;
    while (this.physicsAccum >= this.physicsDt && steps < maxSteps) {
      this.physicsAccum -= this.physicsDt;
      steps++;
    }
    // Prevent spiral of death
    if (steps >= maxSteps) this.physicsAccum = 0;

    return { process: true, physicsSteps: steps, physicsDt: this.physicsDt };
  }

  end_frame(): void {
    this.flush_deferred();
    try {
      this.process_frame.emit(this.frame);
    } catch {
      /* */
    }
    this.input.end_frame();
  }

  emit_physics_frame(): void {
    try {
      this.physics_frame.emit(this.frame);
    } catch {
      /* */
    }
  }

  private flush_deferred(): void {
    const q = this.deferred.slice();
    this.deferred = [];
    for (const fn of q) {
      try {
        fn();
      } catch (e) {
        this.host.log(`Deferred error: ${(e as Error).message}`);
      }
    }
  }

  // ── Script-facing tree API ─────────────────────────────────────

  createTreeAPI(): TreeAPI {
    const self = this;
    return {
      get paused() {
        return self.paused;
      },
      get current_scene() {
        return self.currentPath;
      },
      get root() {
        return '/root';
      },
      get physics_ticks_per_second() {
        return Math.round(1 / self.physicsDt);
      },
      create_timer: (s) => self.create_timer(s),
      create_timer_callback: (s, cb) => self.create_timer_callback(s, cb),
      call_deferred: (fn) => self.call_deferred(fn),
      call_group: (group, method, ...args) => {
        self.callGroupImpl?.(group, method, args);
      },
      get_nodes_in_group: (group) => self.getNodesInGroupImpl?.(group) ?? [],
      change_scene_to_file: async (path) => {
        if (self.changeSceneImpl) return self.changeSceneImpl(path);
        // Fallback: just resolve existence
        return !!loadSceneByPath(path);
      },
      quit: () => {
        self.quitting = true;
        self.setRunning(false);
        self.host.log('SceneTree.quit()');
      },
      set_pause: (p) => self.setPaused(p),
      get_frame: () => self.frame,
    };
  }

  get wants_quit(): boolean {
    return this.quitting;
  }

  /** Load scene resource by path (does not instantiate). */
  load_scene(path: string): SceneResource | null {
    return loadSceneByPath(path);
  }

  dispose(): void {
    this.setRunning(false);
    this.input.dispose();
    this.clear_all_groups();
    this.singletons.clear();
    this.treeSignal.clear();
  }
}
